import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPatch } from '../lib/api';
import { useAuth, useFeature } from '../context/AuthContext';
import { useRefs } from '../context/RefsContext';
import { isDtStaff } from '../lib/staff';
import { V1_PRIORITY_OPTIONS, needsUrgentConfirm, urgentConfirmMessage } from '../lib/priorities';
import {
  TICKET_TYPES,
  clientMoveWarning,
  notifierChoices,
  withClientMove,
  ticketEditBody,
  type TicketEditValues,
  type TicketType,
} from '../lib/ticketEdit';
import type { TicketDetail } from '../types';

interface ClientUser {
  id_user: number;
  nom_user: string;
}

/**
 * Modification du point (formulaire mod_bug du V1) : titre, type, priorité,
 * auteur et notifiés. Agents Digital Team seulement, et seulement si
 * l'interrupteur `ticket_edit` est ouvert. Aucun courriel n'est envoyé (comme
 * le V1). L'auteur et les notifiés sont pris parmi les utilisateurs du client
 * du point ; l'API le contrôle aussi. « Autre client » : l'auteur est choisi
 * chez un autre client, ce qui déplace le point (comme le V1), après
 * confirmation ; l'API le refuse si du temps a déjà été saisi.
 *
 * `authorOnly` : même formulaire réduit au client de l'auteur et à l'auteur,
 * ouvert par le bouton « Modifier » du bloc Auteur (les autres champs ne
 * changent pas, donc ne sont pas envoyés).
 */
export function TicketEditor({
  ticket,
  onChanged,
  authorOnly = false,
}: {
  ticket: TicketDetail;
  onChanged: () => void;
  authorOnly?: boolean;
}) {
  const { user } = useAuth();
  const { traitants, clients } = useRefs();
  const editOpen = useFeature('ticket_edit');
  const navigate = useNavigate();
  const initial: TicketEditValues = useMemo(
    () => ({
      nom_tache: ticket.nom_tache,
      type_tache: ticket.type_tache as TicketType,
      priorite: Number(ticket.priorite),
      id_user: ticket.id_user,
      link_notifier: ticket.notifier_ids,
    }),
    [ticket.nom_tache, ticket.type_tache, ticket.priorite, ticket.id_user, ticket.notifier_ids],
  );
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState<TicketEditValues>(initial);
  const [clientUsers, setClientUsers] = useState<ClientUser[]>([]);
  // Client de l'auteur choisi (celui du point par défaut) : un autre = déplacement.
  const [authorClient, setAuthorClient] = useState<number | null>(ticket.id_client);
  const moving = authorClient !== null && authorClient !== ticket.id_client;
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) {
      setValues(initial);
      setAuthorClient(ticket.id_client);
    }
  }, [initial, editing, ticket.id_client]);

  useEffect(() => {
    if (!editing || authorClient === null) return;
    const ctl = new AbortController();
    apiGet<{ items: ClientUser[] }>(`/api/refs/clients/${authorClient}/users`, ctl.signal)
      .then((r) => {
        setClientUsers(r.items);
        // Autre client : l'auteur actuel n'y est pas, on propose le premier.
        if (authorClient !== ticket.id_client) {
          setValues((v) => (r.items.some((u) => u.id_user === v.id_user) ? v : { ...v, id_user: r.items[0]?.id_user ?? v.id_user }));
        } else {
          setValues((v) => ({ ...v, id_user: initial.id_user }));
        }
      })
      .catch(() => {});
    return () => ctl.abort();
  }, [editing, authorClient, ticket.id_client, initial.id_user]);

  if (!isDtStaff(user) || !editOpen) return null;

  const nameOf = (id: number): string | undefined =>
    clientUsers.find((u) => u.id_user === id)?.nom_user ?? traitants.find((t) => t.id_user === id)?.nom_user;
  const notifiers = notifierChoices(clientUsers, values.id_user, initial.link_notifier, nameOf);
  const authors = moving || clientUsers.some((u) => u.id_user === initial.id_user)
    ? clientUsers
    : [{ id_user: initial.id_user, nom_user: ticket.posteur_nom ?? `Utilisateur #${initial.id_user}` }, ...clientUsers];

  function toggleNotifier(id: number) {
    setValues((v) => ({
      ...v,
      link_notifier: v.link_notifier.includes(id) ? v.link_notifier.filter((n) => n !== id) : [...v.link_notifier, id],
    }));
  }

  async function save() {
    setErr(null);
    const diff = ticketEditBody(initial, values);
    const body = moving ? withClientMove(diff) : diff;
    if (Object.keys(diff).length === 0) {
      setEditing(false);
      return;
    }
    if (body.nom_tache !== undefined && body.nom_tache.length < 3) {
      setErr('Titre trop court (3 caractères minimum)');
      return;
    }
    if (
      body.priorite === 999 &&
      !window.confirm('Priorité 999 « En cours de saisie » : le point sortira des listes (V1 et V2). Continuer ?')
    ) {
      return;
    }
    // Le point devient urgent (≤ 5) : courriels à tous ses traitants, confirmation d'abord.
    if (
      needsUrgentConfirm(body.priorite, initial.priorite) &&
      !window.confirm(urgentConfirmMessage(body.priorite!, 'modification'))
    ) {
      return;
    }
    if (moving) {
      if (body.id_user === undefined || !clientUsers.some((u) => u.id_user === body.id_user)) {
        setErr('Choisissez un auteur du nouveau client');
        return;
      }
      const to = clients.find((c) => c.id_client === authorClient)?.nom_client ?? `client #${authorClient}`;
      if (!window.confirm(clientMoveWarning(ticket.client_nom ?? `client #${ticket.id_client}`, to))) return;
    }
    setSaving(true);
    try {
      const res = await apiPatch<{ traitants?: { message?: string } }>(`/api/tickets/${ticket.display_id}`, body);
      setEditing(false);
      // Changement de client : ce que sont devenus les traitants (retour 16).
      if (moving && res?.traitants?.message) window.alert(res.traitants.message);
      if (body.priorite === 999) navigate('/tickets?preset=a_traiter');
      else onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setSaving(false);
    }
  }

  const label: React.CSSProperties = { display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', marginBottom: 6 };
  const input: React.CSSProperties = {
    width: '100%',
    padding: 5,
    marginTop: 2,
    border: '1px solid var(--border-strong)',
    borderRadius: 5,
    fontSize: 'var(--fs-xs)',
    background: 'var(--bg)',
    color: 'var(--text)',
  };
  const smallBtn: React.CSSProperties = {
    fontSize: 'var(--fs-xs)',
    padding: '4px 10px',
    borderRadius: 4,
    cursor: 'pointer',
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        aria-label={authorOnly ? "Modifier l'auteur du point" : undefined}
        style={{ ...smallBtn, marginTop: 6, padding: '4px 8px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--accent)' }}
      >
        {authorOnly ? '✎ Modifier' : '✎ Modifier le point'}
      </button>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
      style={{ marginTop: 8, borderTop: '1px solid var(--border)', paddingTop: 8 }}
      aria-label={authorOnly ? "Modifier l'auteur du point" : 'Modifier le point'}
    >
      {!authorOnly && (
        <>
        <label style={label}>
          Titre
          <input
            value={values.nom_tache}
            onChange={(e) => setValues((v) => ({ ...v, nom_tache: e.target.value }))}
            required
            minLength={3}
            maxLength={200}
            style={input}
          />
        </label>
        <label style={label}>
          Type
          <select
            value={values.type_tache}
            onChange={(e) => setValues((v) => ({ ...v, type_tache: e.target.value as TicketType }))}
            style={input}
          >
            {TICKET_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label style={label}>
          Priorité
          <select
            value={values.priorite}
            onChange={(e) => setValues((v) => ({ ...v, priorite: Number(e.target.value) }))}
            style={input}
          >
            {!V1_PRIORITY_OPTIONS.some((o) => o.value === initial.priorite) && (
              <option value={initial.priorite}>{initial.priorite} (hors liste du V1)</option>
            )}
            {V1_PRIORITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        </>
      )}
      <label style={label}>
        Client de l’auteur
        <select
          value={authorClient ?? ''}
          onChange={(e) => setAuthorClient(Number(e.target.value))}
          style={input}
        >
          {ticket.id_client !== null && !clients.some((c) => c.id_client === ticket.id_client) && (
            <option value={ticket.id_client}>{ticket.client_nom}</option>
          )}
          {[...clients]
            .sort((a, b) => a.nom_client.localeCompare(b.nom_client))
            .map((c) => (
              <option key={c.id_client} value={c.id_client}>
                {c.nom_client}
                {c.id_client === ticket.id_client ? ' (client actuel)' : ''}
              </option>
            ))}
        </select>
      </label>
      {moving && (
        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--warn, #b45309)', margin: '-2px 0 6px' }}>
          Autre client : le point changera de client (notifiés retirés ; refusé si du temps a déjà été saisi).
        </div>
      )}
      <label style={label}>
        Auteur
        <select
          value={values.id_user}
          onChange={(e) => setValues((v) => ({ ...v, id_user: Number(e.target.value) }))}
          style={input}
        >
          {authors.map((u) => (
            <option key={u.id_user} value={u.id_user}>
              {u.nom_user}
            </option>
          ))}
        </select>
      </label>
      {!moving && !authorOnly && (
        <fieldset style={{ border: 'none', padding: 0, margin: '0 0 8px' }}>
          <legend style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', marginBottom: 4 }}>Notifiés</legend>
          <div style={{ display: 'grid', gap: 4, maxHeight: 180, overflow: 'auto' }}>
            {notifiers.length === 0 && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>Aucun autre utilisateur chez ce client</div>}
            {notifiers.map((n) => (
              <label key={n.id_user} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--fs-xs)' }}>
                <input type="checkbox" checked={values.link_notifier.includes(n.id_user)} onChange={() => toggleNotifier(n.id_user)} />
                {n.nom_user}
              </label>
            ))}
          </div>
        </fieldset>
      )}
      {err && (
        <div role="alert" style={{ color: 'var(--danger)', fontSize: 'var(--fs-xs)', marginBottom: 6 }}>
          {err}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="submit" disabled={saving} style={{ ...smallBtn, background: 'var(--accent)', color: 'white', border: 'none' }}>
          {saving ? '…' : 'Enregistrer'}
        </button>
        <button
          type="button"
          onClick={() => {
            setEditing(false);
            setValues(initial);
            setErr(null);
          }}
          style={{ ...smallBtn, background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)' }}
        >
          Annuler
        </button>
      </div>
      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', marginTop: 6 }}>Aucun courriel n&apos;est envoyé, comme dans le V1.</div>
    </form>
  );
}
